"use client";

/**
 * useSlashCommand — TipTap-aware hook that watches an editor for the "/"
 * trigger character and exposes the state needed to render a floating
 * command palette near the cursor.
 *
 * Single responsibility: detect the trigger, track the query string the
 * user is typing after it, and report viewport coordinates of the "/"
 * position. It does NOT render any UI — that's the popover component's
 * job. This separation lets us swap the UI (or test the trigger logic in
 * isolation) without touching the slash detection.
 *
 * Trigger rule: a "/" at column 0 of the current text block, or after
 * whitespace, optionally followed by [a-zA-Z0-9-]* (the query). The query
 * stops as soon as the user types anything outside that character class
 * or moves the selection outside the trigger range.
 */

import { useCallback, useEffect, useState, type RefObject } from "react";
import type { Editor } from "@tiptap/react";

interface SlashState {
  open: boolean;
  /** Doc position of the leading "/" */
  rangeFrom: number;
  /** Doc position of the cursor (one past the last query char) */
  rangeTo: number;
  /** Query string after the "/" — empty when user just typed "/" */
  query: string;
  /** Viewport-relative coordinates to anchor the popover */
  position: { top: number; left: number };
}

const CLOSED: SlashState = {
  open: false,
  rangeFrom: 0,
  rangeTo: 0,
  query: "",
  position: { top: 0, left: 0 },
};

interface UseSlashCommandResult {
  open: boolean;
  query: string;
  position: { top: number; left: number };
  /** Replace the trigger range (`/query`) with empty content. Call this
   *  before applying a block-insert action so the action lands cleanly. */
  consumeTrigger: () => void;
  close: () => void;
}

/**
 * @param editor      Active TipTap editor (or null while it boots).
 * @param containerRef Optional ref to a positioned element (e.g. the
 *   `.simple-editor-wrapper` div). When provided, the returned `position`
 *   is expressed relative to that element's top-left, so the popover can
 *   be rendered as an `absolute` child of it. When omitted, viewport
 *   coordinates are returned (use with `position: fixed`). Wrapper-
 *   relative is required when the popover lives inside an ancestor that
 *   sets a transform/perspective/filter (e.g. Radix Dialog content),
 *   because such ancestors break `position: fixed` viewport anchoring.
 */
export function useSlashCommand(
  editor: Editor | null,
  containerRef?: RefObject<HTMLElement | null>,
): UseSlashCommandResult {
  const [state, setState] = useState<SlashState>(CLOSED);

  useEffect(() => {
    if (!editor) return;

    const recomputeFromSelection = () => {
      const { selection } = editor.state;
      if (!selection.empty) {
        setState((prev) => (prev.open ? CLOSED : prev));
        return;
      }

      const { from } = selection;
      const $pos = editor.state.doc.resolve(from);
      // Text from start of current text block up to the cursor
      const textBefore = $pos.parent.textBetween(
        0,
        $pos.parentOffset,
        undefined,
        "\ufffc",
      );

      // Trigger: "/" at start of block OR after whitespace, then optional
      // [a-zA-Z0-9-]* query.
      const match = textBefore.match(/(?:^|\s)(\/[a-zA-Z0-9-]*)$/);
      if (!match) {
        setState((prev) => (prev.open ? CLOSED : prev));
        return;
      }

      const trigger = match[1];
      const query = trigger.slice(1);
      const rangeFrom = from - trigger.length;
      const rangeTo = from;

      const coords = editor.view.coordsAtPos(rangeFrom);
      let top = coords.bottom + 4;
      let left = coords.left;
      const containerEl = containerRef?.current;
      if (containerEl) {
        const rect = containerEl.getBoundingClientRect();
        // Account for the container's own scroll position so the popover
        // sticks to the cursor as the user scrolls the editor body.
        top = top - rect.top + containerEl.scrollTop;
        left = left - rect.left + containerEl.scrollLeft;
      }

      setState({
        open: true,
        rangeFrom,
        rangeTo,
        query,
        position: { top, left },
      });
    };

    editor.on("update", recomputeFromSelection);
    editor.on("selectionUpdate", recomputeFromSelection);
    return () => {
      editor.off("update", recomputeFromSelection);
      editor.off("selectionUpdate", recomputeFromSelection);
    };
  }, [editor, containerRef]);

  const consumeTrigger = useCallback(() => {
    if (!editor || !state.open) return;
    editor
      .chain()
      .focus()
      .deleteRange({ from: state.rangeFrom, to: state.rangeTo })
      .run();
    setState(CLOSED);
  }, [editor, state.open, state.rangeFrom, state.rangeTo]);

  const close = useCallback(() => {
    setState((prev) => (prev.open ? CLOSED : prev));
  }, []);

  return {
    open: state.open,
    query: state.query,
    position: state.position,
    consumeTrigger,
    close,
  };
}
